import React from 'react';
import { Box, Menu, MenuItem, Tooltip } from '@mui/material';

// ── Reading-room HUD dock ───────────────────────────────────────────────────
// Floating glass toolbar over the viewer (viewfinder-style). No boxes: text
// segments + stroke glyphs, one cyan accent. Inline SVGs — @mui/icons-material
// is not a declared dependency of this monorepo.

const ACCENT = '#6FE3EA';
const MUTED = '#8FA8C0';
const HAIRLINE = 'rgba(122, 160, 199, 0.25)';

const glyphProps = {
  width: 15,
  height: 15,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const GridGlyph = ({ cols }: { cols: 1 | 2 | 3 | 4 }) => {
  const rects: { x: number; y: number; w: number }[] = [];
  const gap = 1.5;
  const size = (16 - 2 - gap * (cols - 1)) / cols;
  for (let r = 0; r < cols; r++) {
    for (let c = 0; c < cols; c++) {
      rects.push({ x: 1 + c * (size + gap), y: 1 + r * (size + gap), w: size });
    }
  }
  return (
    <svg {...glyphProps}>
      {rects.map((r, i) => (
        <rect
          key={i}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.w}
          rx="0.8"
        />
      ))}
    </svg>
  );
};

const PlayGlyph = () => (
  <svg {...glyphProps}>
    <path d="M5 3.2v9.6l7.4-4.8L5 3.2z" />
  </svg>
);

const PauseGlyph = () => (
  <svg {...glyphProps}>
    <path d="M5.5 3.5v9M10.5 3.5v9" />
  </svg>
);

/**
 * Whether the Overview button appears in the express viewer.
 *
 * Off for this release. Kept as a single named flag rather than deleting the button so
 * re-enabling it is a one-line change once clinics are exporting DICOM View Names.
 */
const OVERVIEW_ENABLED = false;

const OverviewGlyph = () => (
  <svg {...glyphProps}>
    <rect
      x="1.2"
      y="1.2"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="6"
      y="1.2"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="10.8"
      y="1.2"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="1.2"
      y="6"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="6"
      y="6"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="10.8"
      y="6"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="1.2"
      y="10.8"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="6"
      y="10.8"
      width="4"
      height="4"
      rx="0.8"
    />
    <rect
      x="10.8"
      y="10.8"
      width="4"
      height="4"
      rx="0.8"
    />
  </svg>
);

const CompareGlyph = () => (
  <svg {...glyphProps}>
    <rect
      x="1.2"
      y="3"
      width="6"
      height="10"
      rx="1"
    />
    <rect
      x="8.8"
      y="3"
      width="6"
      height="10"
      rx="1"
    />
  </svg>
);

// Shared button reset: borderless, quiet, cyan on hover/active.
const hudBtnSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
  border: 'none',
  background: 'transparent',
  color: MUTED,
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '0.02em',
  lineHeight: 1,
  px: 1.25,
  py: 0.9,
  borderRadius: 99,
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  transition: 'color 120ms ease, background 120ms ease',
  '&:hover': { color: ACCENT, background: 'rgba(111, 227, 234, 0.08)' },
  '&:focus-visible': { outline: `1px solid ${ACCENT}`, outlineOffset: 1 },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
};

const hudActiveSx = {
  color: '#062028',
  background: ACCENT,
  '&:hover': { color: '#062028', background: ACCENT },
};

const RefreshGlyph = () => (
  <svg {...glyphProps}>
    <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 1.5v3h-3" />
  </svg>
);

// Small caret so the speed button reads as a menu.
const CaretGlyph = () => (
  <svg
    {...glyphProps}
    width={9}
    height={9}
  >
    <path d="M3.5 6 8 10.5 12.5 6" />
  </svg>
);

const Divider = () => (
  <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.75, background: HAIRLINE, flexShrink: 0 }} />
);

// Playback speeds offered for a clip. 0.5x is the Doppler default.
const SPEED_PRESETS = [0.25, 0.5, 0.75, 1, 1.5, 2];

interface ViewerControlsBarProps {
  selectedStageType: string;
  onStageTypeChange: (stage: string) => void;
  onViewTypeChange: (view: number | string) => void;
  selectedViewType: number | string;
  viewList: string[];
  isPlaying: boolean;
  togglePlayAll: () => void;
  isSizeToScreen: boolean;
  setIsSizeToScreen: (v: boolean) => void;
  setGridSize: (v: string) => void;
  currentGridSize: string;
  onCompare?: () => void;
  /** Opens the nine-view study overview. */
  onOverview?: () => void;
  /** Playback rate of the clip currently in view. */
  playbackRate?: number;
  /** Persist a new rate for that clip. Omit to hide the speed control. */
  onPlaybackRateChange?: (rate: number) => void;
  /** Reloads the study's images. Omit to hide the button. */
  onRefreshImages?: () => void;
}

// Thumbnail-grid presets, mirrored from the old "Grid Size" dropdown.
const GRID_PRESETS: { cols: 2 | 3 | 4; label: string; value: string }[] = [
  { cols: 2, label: 'Small grid', value: '47%' },
  { cols: 3, label: 'Medium grid', value: '30%' },
  { cols: 4, label: 'Large grid', value: '23%' },
];

const ViewerControlsBar = ({
  selectedStageType,
  onStageTypeChange,
  onViewTypeChange,
  selectedViewType,
  viewList,
  isPlaying,
  togglePlayAll,
  isSizeToScreen,
  setIsSizeToScreen,
  setGridSize,
  currentGridSize,
  onCompare,
  onOverview,
  playbackRate,
  onPlaybackRateChange,
  onRefreshImages,
}: ViewerControlsBarProps) => {
  const isNormal = selectedStageType === 'NORMAL';
  const stressViewIdx = Number(selectedViewType) || 0;
  const [speedAnchor, setSpeedAnchor] = React.useState<HTMLElement | null>(null);
  const rate = typeof playbackRate === 'number' && playbackRate > 0 ? playbackRate : 1;

  return (
    <Box
      sx={{
        // Top control bar (per Dr Shashi/Nikhil): all controls on top, next to
        // the Echo/Stress buttons, so the bar is visible on first load and sized
        // to its content — was a floating bottom dock that sat below the fold.
        // Sticky so it pins under the (also sticky) patient-name strip on the
        // main scroll instead of sliding over it; z below the strip's z-20.
        position: 'sticky',
        top: 70,
        zIndex: 15,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.25,
        mb: 1,
        px: 0.75,
        py: 0.4,
        borderRadius: 99,
        background: 'rgba(6, 18, 34, 0.8)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid ${HAIRLINE}`,
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
        width: 'fit-content',
        maxWidth: '100%',
        overflowX: 'auto',
        '@media (prefers-reduced-motion: no-preference)': {
          animation: 'hudRise 220ms ease-out',
        },
        '@keyframes hudRise': {
          from: { opacity: 0, transform: 'translateY(-6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Echo / Stress segments */}
      <Box
        component="button"
        type="button"
        onClick={() => {
          onStageTypeChange('NORMAL');
          onViewTypeChange('');
        }}
        sx={{ ...hudBtnSx, ...(isNormal ? hudActiveSx : {}) }}
      >
        Echo
      </Box>
      <Box
        component="button"
        type="button"
        onClick={() => {
          if (isNormal) {
            onStageTypeChange('STRESS');
            onViewTypeChange('');
          }
        }}
        sx={{ ...hudBtnSx, ...(!isNormal ? hudActiveSx : {}) }}
      >
        Stress
      </Box>
      {!isNormal && (
        <>
          <Divider />
          <Box
            component="button"
            type="button"
            onClick={() => onStageTypeChange('STRESS')}
            sx={{ ...hudBtnSx, ...(selectedStageType === 'STRESS' ? { color: ACCENT } : {}) }}
          >
            Views
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => onStageTypeChange('QUAD')}
            sx={{ ...hudBtnSx, ...(selectedStageType === 'QUAD' ? { color: ACCENT } : {}) }}
          >
            Quad
          </Box>
        </>
      )}

      {/* Stress view stepper */}
      {selectedStageType === 'STRESS' && viewList.length > 0 && (
        <>
          <Divider />
          <Box
            component="button"
            type="button"
            onClick={() => onViewTypeChange(stressViewIdx - 1)}
            disabled={stressViewIdx === 0}
            aria-label="Previous view"
            sx={{ ...hudBtnSx, px: 1, fontSize: 15 }}
          >
            ‹
          </Box>
          <Box
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: ACCENT,
              minWidth: 44,
              textAlign: 'center',
            }}
          >
            {viewList[stressViewIdx] || ''}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => onViewTypeChange(stressViewIdx + 1)}
            disabled={stressViewIdx >= viewList.length - 1}
            aria-label="Next view"
            sx={{ ...hudBtnSx, px: 1, fontSize: 15 }}
          >
            ›
          </Box>
        </>
      )}

      {/* Layout glyphs (normal echo only) */}
      {isNormal && (
        <>
          <Divider />
          <Tooltip title="Fit one image">
            <Box
              component="button"
              type="button"
              onClick={() => setIsSizeToScreen(true)}
              aria-label="Single image, fit to screen"
              sx={{ ...hudBtnSx, px: 1, ...(isSizeToScreen ? { color: ACCENT } : {}) }}
            >
              <GridGlyph cols={1} />
            </Box>
          </Tooltip>
          {GRID_PRESETS.map(p => (
            <Tooltip
              key={p.value}
              title={p.label}
            >
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setIsSizeToScreen(false);
                  setGridSize(p.value);
                }}
                aria-label={p.label}
                sx={{
                  ...hudBtnSx,
                  px: 1,
                  ...(!isSizeToScreen && currentGridSize === p.value ? { color: ACCENT } : {}),
                }}
              >
                <GridGlyph cols={p.cols} />
              </Box>
            </Tooltip>
          ))}
        </>
      )}

      <Divider />

      {/* Play / pause all loops */}
      <Box
        component="button"
        type="button"
        onClick={togglePlayAll}
        aria-label={isPlaying ? 'Pause all loops' : 'Play all loops'}
        sx={{ ...hudBtnSx, ...(isPlaying ? { color: ACCENT } : {}) }}
      >
        {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
        {isPlaying ? 'Pause' : 'Play'}
      </Box>

      {/* Playback speed for the clip in view — saved per clip, for everyone */}
      {onPlaybackRateChange && (
        <>
          <Tooltip title="Playback speed for this clip (saved)">
            <Box
              component="button"
              type="button"
              onClick={e => setSpeedAnchor(e.currentTarget)}
              aria-label="Playback speed"
              aria-haspopup="menu"
              sx={{ ...hudBtnSx, gap: 0.4, ...(rate !== 1 ? { color: ACCENT } : {}) }}
            >
              {rate}x
              <CaretGlyph />
            </Box>
          </Tooltip>
          <Menu
            anchorEl={speedAnchor}
            open={Boolean(speedAnchor)}
            onClose={() => setSpeedAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  background: 'rgba(6, 18, 34, 0.96)',
                  border: `1px solid ${HAIRLINE}`,
                  backdropFilter: 'blur(10px)',
                  minWidth: 92,
                },
              },
            }}
          >
            {SPEED_PRESETS.map(preset => (
              <MenuItem
                key={preset}
                selected={preset === rate}
                onClick={() => {
                  onPlaybackRateChange(preset);
                  setSpeedAnchor(null);
                }}
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: preset === rate ? ACCENT : MUTED,
                  '&.Mui-selected, &.Mui-selected:hover': {
                    background: 'rgba(111, 227, 234, 0.12)',
                  },
                  '&:hover': { color: ACCENT, background: 'rgba(111, 227, 234, 0.08)' },
                }}
              >
                {preset}x
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Overview — the nine standard views in one grid.
          Hidden pending the view-labelling work: the grid slots each clip by its DICOM
          View Name, and most carts are not exporting one, so it presents as mostly empty
          to a clinician with no indication that the data — not the feature — is missing.
          Flip OVERVIEW_ENABLED to bring it back; the panel itself is untouched. */}
      {OVERVIEW_ENABLED && onOverview && isNormal && (
        <Box
          component="button"
          type="button"
          onClick={onOverview}
          sx={hudBtnSx}
        >
          <OverviewGlyph />
          Overview
        </Box>
      )}

      {/* Compare */}
      {onCompare && isNormal && (
        <Box
          component="button"
          type="button"
          onClick={onCompare}
          sx={hudBtnSx}
        >
          <CompareGlyph />
          Compare
        </Box>
      )}

      {/* Refresh images — sits with the image controls, apart from Regenerate report */}
      {onRefreshImages && (
        <>
          <Divider />
          <Tooltip title="Reload this study's images">
            <Box
              component="button"
              type="button"
              onClick={onRefreshImages}
              sx={hudBtnSx}
            >
              <RefreshGlyph />
              Refresh images
            </Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default ViewerControlsBar;
